import { Badge, makeStyles, Spinner, Tab, TabList, tokens } from '@fluentui/react-components';
import { Board24Regular, Document24Regular, Eye24Regular, Globe24Regular, Money24Regular, Shield24Regular } from '@fluentui/react-icons';
import React, { useMemo, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import { ECKDATEN_TAB_REQUIRED_FIELDS, getVisibleMissingEckdatenFields } from '../../utils/eckdatenCompleteness';
import EckdatenAllgemein from './EckdatenAllgemein';
import EckdatenESGFunding from './EckdatenESGFunding';
import EckdatenFinanzielles from './EckdatenFinanzielles';
import EckdatenInternalOversight from './EckdatenInternalOversight';
import EckdatenOverview from './EckdatenOverview';
import EckdatenWebsite from './EckdatenWebsite';

const useStyles = makeStyles({
    container: {
        padding: `0 ${tokens.spacingHorizontalL}`,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        paddingTop: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: tokens.spacingVerticalL,
    },
    tabsContainer: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: tokens.spacingVerticalL,
    },
    title: {
        margin: 0,
        marginBottom: tokens.spacingVerticalM,
        fontSize: tokens.fontSizeHero800,
    },
    content: {
        flex: 1,
        overflow: 'auto',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
});

const Eckdaten: React.FC = () => {
    const styles = useStyles();
    const { project, isLoading, error, userInfo } = useProject();
    const [selectedTab, setSelectedTab] = useState<string>('overview');
    const missingFields = useMemo(() => getVisibleMissingEckdatenFields(project, userInfo), [project, userInfo]);
    const missingCountByTab = (tabKey: string) =>
        (ECKDATEN_TAB_REQUIRED_FIELDS[tabKey] ?? []).filter((key) => missingFields.includes(key)).length;
    const tabLabel = (tabKey: string, label: string) => {
        const count = missingCountByTab(tabKey);
        return count > 0 ? (
            <>
                {label}
                <Badge appearance="filled" color="danger" size="small" style={{ marginLeft: tokens.spacingHorizontalXS }}>
                    {count}
                </Badge>
            </>
        ) : label;
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading Project..." labelPosition="below" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorContainer}>
                    <h2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {error}
                    </h2>
                </div>
            </div>
        );
    }

    const renderContent = () => {
        switch (selectedTab) {
            case 'overview':
                return <EckdatenOverview project={project} />;
            case 'allgemein':
                return <EckdatenAllgemein project={project} />;
            case 'esgfunding':
                return <EckdatenESGFunding project={project} />;
            case 'internaloversight':
                return <EckdatenInternalOversight project={project} />;
            case 'finanzielles':
                return <EckdatenFinanzielles project={project} />;
            case 'website':
                return <EckdatenWebsite project={project} />;
            default:
                return <EckdatenOverview project={project} />;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.tabsContainer}>
                <TabList selectedValue={selectedTab} onTabSelect={(_event, data) => setSelectedTab(data.value as string)}>
                    <Tab value="overview" icon={<Eye24Regular />}>Overview</Tab>
                    <Tab value="allgemein" icon={<Document24Regular />}>{tabLabel('allgemein', 'General')}</Tab>
                    <Tab value="esgfunding" icon={<Globe24Regular />}>{tabLabel('esgfunding', 'ESG & Funding')}</Tab>
                    <Tab value="finanzielles" icon={<Money24Regular />}>Financial</Tab>
                    <Tab value="internaloversight" icon={<Shield24Regular />}>{tabLabel('internaloversight', 'Internal Oversight')}</Tab>
                    <Tab value="website" icon={<Board24Regular />}>Website</Tab>
                </TabList>
            </div>
            <div className={styles.content}>
                {renderContent()}
            </div>
        </div>
    );
};

export default Eckdaten;
